// next-app/src/store/auth-store.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Dealer } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface AuthState {
  // Persisted state
  loginEmail: string;
  twoFACode: string;
  dealer: Dealer | null;
  dealerToken: string;

  // Non-persisted state (password should never be stored)
  loginPassword: string;

  // Hydration state (tracks if store has loaded from localStorage)
  isHydrated: boolean;
}

interface AuthActions {
  setLoginEmail: (email: string) => void;
  setLoginPassword: (password: string) => void;
  setTwoFACode: (code: string) => void;
  setDealer: (dealer: Dealer | null) => void;
  setDealerToken: (token: string) => void;
  doLogout: () => void;
  setHydrated: () => void;

  // Computed getters
  isAuthenticated: () => boolean;
}

type AuthStore = AuthState & AuthActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  loginEmail: '',
  twoFACode: '',
  dealer: null,
  dealerToken: '',
  loginPassword: '',
  isHydrated: false,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      ...initialState,

      // Actions
      setLoginEmail: (email: string) => set({ loginEmail: email }),

      setLoginPassword: (password: string) => set({ loginPassword: password }),

      setTwoFACode: (code: string) => set({ twoFACode: code }),

      setDealer: (dealer: Dealer | null) => set({ dealer }),

      setDealerToken: (token: string) => set({ dealerToken: token }),

      doLogout: () =>
        set({
          dealerToken: '',
          dealer: null,
          loginPassword: '', // Clear password on logout
        }),

      setHydrated: () => set({ isHydrated: true }),

      // Computed
      isAuthenticated: () => {
        const state = get();
        return Boolean(state.dealerToken && state.dealer);
      },
    }),
    {
      name: 'auth-store', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields - exclude loginPassword for security
      partialize: (state) => ({
        loginEmail: state.loginEmail,
        twoFACode: state.twoFACode,
        dealer: state.dealer,
        dealerToken: state.dealerToken,
      }),
      // Set hydrated flag when store is rehydrated from localStorage
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    }
  )
);

// ============================================================================
// Selectors (for optimized re-renders)
// ============================================================================

export const selectDealer = (state: AuthStore) => state.dealer;
export const selectToken = (state: AuthStore) => state.dealerToken;
export const selectIsAuthenticated = (state: AuthStore) =>
  Boolean(state.dealerToken && state.dealer);
export const selectIsHydrated = (state: AuthStore) => state.isHydrated;
